// Per-user notification + display preference contracts (PRD #260 / ADR-0013).
//
// These replace:
//   - Ops Settings firmConfig.saveNotifications (firm-global, wrong tier)
//   - Desk Settings localStorage notification toggles (per-device, wrong tier)
//   - Comply Settings static arrays (not saved at all)
//   - Browser-cookie theme/density (per-device)
//
// All preferences are now per-user, server-side. Absence of a row = defaults
// (ADR-0013 semantics). Endpoints are persona-admitted to any staff user ('*');
// the controller enforces per-user scoping via the session actor.

import { initContract } from '@ts-rest/core';
import { z } from 'zod';

const c = initContract();

// ---------------------------------------------------------------------------
// Notification preferences schemas
// ---------------------------------------------------------------------------

export const severityFilterSchema = z.enum(['critical', 'attention', 'info']);
export type SeverityFilter = z.infer<typeof severityFilterSchema>;

export const notificationPrefsSchema = z.object({
    digestEnabled: z.boolean(),
    digestTime: z.string(),
    /** Per-agent-family toggle map. An absent key means on; explicit false mutes the family. */
    agentToggles: z.record(z.boolean()),
    /** Minimum severity tier to include: critical-only, attention+, or all (info+). */
    severityFilter: severityFilterSchema,
    updatedAt: z.string(),
});
export type NotificationPrefsDto = z.infer<typeof notificationPrefsSchema>;

export const notificationPrefsPatchSchema = z.object({
    digestEnabled: z.boolean().optional(),
    /** HH:MM 24-hour format. */
    digestTime: z
        .string()
        .regex(/^\d{2}:\d{2}$/)
        .optional(),
    agentToggles: z.record(z.boolean()).optional(),
    severityFilter: severityFilterSchema.optional(),
});
export type NotificationPrefsPatchDto = z.infer<typeof notificationPrefsPatchSchema>;

// ---------------------------------------------------------------------------
// Display preferences schemas
// ---------------------------------------------------------------------------

export const themeSchema = z.enum(['light', 'dark', 'system']);
export type Theme = z.infer<typeof themeSchema>;

export const densitySchema = z.enum(['comfortable', 'compact']);
export type Density = z.infer<typeof densitySchema>;

export const displayPrefsSchema = z.object({
    theme: themeSchema,
    density: densitySchema,
    updatedAt: z.string(),
});
export type DisplayPrefsDto = z.infer<typeof displayPrefsSchema>;

export const displayPrefsPatchSchema = z.object({
    theme: themeSchema.optional(),
    density: densitySchema.optional(),
});
export type DisplayPrefsPatchDto = z.infer<typeof displayPrefsPatchSchema>;

// ---------------------------------------------------------------------------
// Router
// ---------------------------------------------------------------------------

export const userPrefsContract = c.router(
    {
        getNotificationPrefs: {
            method: 'GET',
            path: '/user-prefs/notifications',
            responses: {
                200: notificationPrefsSchema,
            },
            summary:
                "Read the signed-in user's notification preferences. Returns defaults when no row exists.",
        },
        putNotificationPrefs: {
            method: 'PUT',
            path: '/user-prefs/notifications',
            body: notificationPrefsPatchSchema,
            responses: {
                200: notificationPrefsSchema,
            },
            summary: "Save the signed-in user's notification preferences. Per-user, not firm-wide.",
        },
        getDisplayPrefs: {
            method: 'GET',
            path: '/user-prefs/display',
            responses: {
                200: displayPrefsSchema,
            },
            summary:
                "Read the signed-in user's display preferences (theme, density). Returns defaults when no row exists.",
        },
        putDisplayPrefs: {
            method: 'PUT',
            path: '/user-prefs/display',
            body: displayPrefsPatchSchema,
            responses: {
                200: displayPrefsSchema,
            },
            summary:
                "Save the signed-in user's display preferences. Replaces the browser-cookie store.",
        },
    },
    {
        pathPrefix: '/api',
        strictStatusCodes: false,
    },
);
